import { NavLink } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Home,
  Users,
  Activity,
  Cpu,
  Settings,
  Bot,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: Home, end: true },
  { to: '/agents', label: 'Agents', icon: Users },
  { to: '/activity', label: 'Activity', icon: Activity },
  { to: '/system', label: 'System', icon: Cpu },
];

export const SETTINGS_ITEM: NavItem = {
  to: '/settings',
  label: 'Settings',
  icon: Settings,
};

/**
 * Sidebar — fixed 220px on desktop. Logo at top, 4 primary nav items,
 * settings pinned at bottom. Mobile uses `BottomTabBar` instead.
 */
export function Sidebar() {
  return (
    <aside
      className="sticky top-0 hidden h-screen w-[220px] flex-shrink-0 flex-col border-r border-border bg-card md:flex"
      aria-label="Primary navigation"
    >
      <div className="flex h-14 items-center gap-2 border-b border-border px-4">
        <div className="flex h-7 w-7 items-center justify-center rounded bg-primary text-primary-foreground">
          <Bot className="h-4 w-4" aria-hidden />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold">AgentHub</span>
          <span className="text-[11px] text-muted-foreground">
            Mission Control
          </span>
        </div>
      </div>

      <nav className="flex-1 space-y-0.5 px-2 py-3">
        {NAV_ITEMS.map((item) => (
          <SidebarLink key={item.to} item={item} />
        ))}
      </nav>

      <div className="space-y-0.5 border-t border-border px-2 py-3">
        <SidebarLink item={SETTINGS_ITEM} />
      </div>
    </aside>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )
      }
    >
      <item.icon className="h-4 w-4" aria-hidden />
      <span>{item.label}</span>
    </NavLink>
  );
}

/**
 * BottomTabBar — mobile replacement for the sidebar. §8.9 spec: no
 * hamburger; Settings is surfaced via the overflow menu / TopBar.
 */
export function BottomTabBar() {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-card md:hidden"
      aria-label="Primary navigation"
    >
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              'flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium',
              isActive ? 'text-primary' : 'text-muted-foreground',
            )
          }
        >
          <item.icon className="h-5 w-5" aria-hidden />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
